/**
 * V33-17 - ARCHIVE
 */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { isFulfilled } from "@reduxjs/toolkit";

import MessageDialog from "components/__reusable/MessageDialog";
import PasswordConfirmationDialog from "components/__reusable/PasswordConfirmDialog";
import SecondaryActionButton from "components/__reusable/SecondaryActionButton";
import PrimaryActionButton from "components/__reusable/PrimaryActionButton";
import useAppSelector from "hooks/useAppSelector";
import useAppDispatch from "hooks/useAppDispatch";
import { deleteStudent } from "features/student/studentAction";
import { resetSelectedStudents } from "features/student/studentSlice";
import { setSuccessStatus } from "features/success/successSlice";
import { setErrorStatus } from "features/error/errorSlice";
import { SupportedLang } from "config/constants";
import { getDeletionToken, deleteStudentWithToken } from "services/studentServices";

interface DeleteStudentPromptDialogProps {
  onYesClick: () => void;
  onNoClick: () => void;
}

const DeleteStudentPromptDialog: React.FC<DeleteStudentPromptDialogProps> = ({
  onNoClick,
  onYesClick,
}) => {
  const { t, i18n } = useTranslation();

  const { selectedStudents } = useAppSelector((state) => state.student);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(true);
  const [showFinalConfirmation, setShowFinalConfirmation] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const dispatch = useAppDispatch();

  const renderStudentName = () => {
    const studentNames = [];
    for (let student of selectedStudents) {
      studentNames.push(`${student.last_name} ${student.first_name}`);
    }
    const seperator = i18n.language === SupportedLang.ja ? "と" : ", ";
    return studentNames.join(seperator);
  };

  const handlePasswordConfirm = async (password: string) => {
    setTokenLoading(true);
    try {
      const token = await getDeletionToken(password, "delete_student");
      localStorage.setItem("deletion_token", token);
      setShowPasswordPrompt(false);
      setShowFinalConfirmation(true);
    } catch (error: any) {
      dispatch(
        setErrorStatus(error.response?.data?.message || "パスワードが正しくありません")
      );
    } finally {
      setTokenLoading(false);
    }
  };

  const handleCancel = () => {
    setShowPasswordPrompt(false);
    setShowFinalConfirmation(false);
    localStorage.removeItem("deletion_token");
    onNoClick();
  };

  const remove = async () => {
    const token = localStorage.getItem("deletion_token");
    if (!token) {
      dispatch(setErrorStatus("認証トークンが見つかりません"));
      setShowFinalConfirmation(false);
      onYesClick();
      return;
    }

    setDeleteLoading(true);
    try {
      let successCount = 0;
      for (let student of selectedStudents) {
        try {
          await deleteStudentWithToken(student.id, token);
          successCount += 1;
        } catch (error) {
          // Continue with other students even if one fails
        }
      }
      
      localStorage.removeItem("deletion_token");
      
      if (successCount === selectedStudents.length) {
        dispatch(resetSelectedStudents());
        dispatch(
          setSuccessStatus({
            title: "_student_form._delete_student_success",
            message: "_student_form._delete_student_success_content",
          })
        );
      } else {
        dispatch(setErrorStatus("一部の園児の削除に失敗しました"));
      }
      
      setShowFinalConfirmation(false);
      onYesClick();
    } catch (error: any) {
      setShowFinalConfirmation(false);
      dispatch(
        setErrorStatus(error.response?.data?.message || "エラーが発生しました")
      );
      onYesClick();
    } finally {
      setDeleteLoading(false);
    }
  };

  if (showPasswordPrompt) {
    return (
      <PasswordConfirmationDialog
        onCancel={handleCancel}
        onConfirm={handlePasswordConfirm}
        isLoading={tokenLoading}
        title={t("_student._delete_student", { defaultValue: "園児を削除する" })}
        message={
          <div className="space-y-3">
            <p>
              {t("_student._deletion_warning_1", {
                defaultValue:
                  "園児を削除すると、該当園児の写真データがすべて削除され、保護者アプリからも写真が閲覧できなくなります。",
              })}
            </p>
            <p>
              {t("_student._deletion_warning_2", {
                defaultValue: "削除した園児のデータは復元することができません。",
              })}
            </p>
            <p className="pt-2">
              {t("_student._deletion_proceed_instructions", {
                defaultValue:
                  "削除処理に進む場合は施設のパスワードを入力し、「同意して進む」を選択してください。",
              })}
            </p>
          </div>
        }
      />
    );
  }

  if (showFinalConfirmation) {
    return (
      <MessageDialog>
        <div className="space-y-3">
          <h2 className="text-2xl font-semibold text-center">
            {t("_student._delete_warning", {
              name: renderStudentName(),
            })}
          </h2>
          <div className="flex justify-between items-center space-x-5">
            <div className="w-1/2">
              <SecondaryActionButton
                label={t("_generic._no")}
                onClick={handleCancel}
              />
            </div>
            <div className="w-1/2">
              <PrimaryActionButton
                label={t("_generic._yes")}
                loading={deleteLoading}
                onClick={remove}
              />
            </div>
          </div>
        </div>
      </MessageDialog>
    );
  }

  return null;
};

export default DeleteStudentPromptDialog;
